/**
 * Registry of errors that a *local* mutation `onError` callback has already
 * surfaced to the user (toast / inline dialog message), so the global
 * `MutationCache.onError` handler installed by `createAppQueryClient` does
 * not show a second, redundant toast for the same failure.
 *
 * Why this is needed: TanStack Query calls the **cache-level** `onError`
 * before the **observer-level** callbacks passed to `mutate(variables,
 * { onError })`, and the `Mutation` object the cache handler receives does
 * not expose those observer callbacks. The global handler therefore defers
 * its toast by one macrotask and checks this registry; local callbacks mark
 * the error here first (synchronously, before any `await`).
 */

const locallyHandledErrors = new WeakSet<object>();

/**
 * Marks an error as already surfaced to the user by a local error handler.
 * Must be called synchronously at the very top of the local `onError`
 * callback — before any `await` — so the mark is in place before the global
 * handler's deferred check runs.
 */
export function markErrorHandledLocally(error: unknown): void {
  if (error !== null && typeof error === 'object') {
    locallyHandledErrors.add(error);
  }
}

/** True when a local error handler already surfaced this error to the user. */
export function wasErrorHandledLocally(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    locallyHandledErrors.has(error)
  );
}
